'use client';

import React from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import useProfileStrategies from 'src/hooks/strategy/useProfileStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useGetStrategyMetricsQuery } from 'src/api/redux';
import { Drawer } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';

export const HEATMAP_DRAWER_ID = 'heatmap-metrics';

interface HeatmapControlsProps {
  engagedId: string;
}

const HeatmapControls: React.FC<HeatmapControlsProps> = ({ engagedId }) => {
  const {
    showHeatmap,
    setShowHeatmap,
    selectedHeatmapMetric,
    setSelectedHeatmapMetric,
  } = useProfileStrategies();
  const { isDrawerOpen, closeDrawer } = useDrawer();

  const isOpen = isDrawerOpen(HEATMAP_DRAWER_ID);

  // Fetch strategy metrics to determine the correct sort field
  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'engage',
      strategyId: engagedId.toString(),
    },
    {
      skip: !engagedId,
    }
  );

  // Flatten metrics for easier access, including direction in display label
  const flatMetrics =
    strategyMetrics?.results?.flatMap((metric) => {
      const direction = metric.execution_params?.direction;
      const displayLabel = direction
        ? `${metric.label} (${direction})`
        : metric.label;

      return metric.display_options.map((option) => ({
        ...option,
        metricLabel: displayLabel,
        metricId: metric.id,
      }));
    }) || [];

  // Group metrics by their id to avoid duplicate label collisions
  type GroupedMetric = { label: string; options: typeof flatMetrics };
  const groupedItems: Record<number, GroupedMetric> = {};
  flatMetrics.forEach((item) => {
    const key = item.metricId;
    if (!groupedItems[key]) {
      groupedItems[key] = { label: item.metricLabel, options: [] };
    }
    groupedItems[key].options.push(item);
  });

  const handleSelectMetric = (opt: typeof flatMetrics[0]) => {
    const isCurrentlySelected = selectedHeatmapMetric?.code === opt.code;
    if (isCurrentlySelected) {
      setSelectedHeatmapMetric(null);
      setShowHeatmap(false);
    } else {
      setSelectedHeatmapMetric({ code: opt.code, label: opt.label });
      setShowHeatmap(true);
    }
    handleClose();
  };

  const handleSelectAllOpportunities = () => {
    setShowHeatmap(true);
    setSelectedHeatmapMetric(null);
    handleClose();
  };

  // Check if "All Opportunities" is currently selected (heatmap on, no metric)
  const isAllOpportunitiesSelected = showHeatmap && !selectedHeatmapMetric;

  const handleTurnOffHeatmap = () => {
    setShowHeatmap(false);
    setSelectedHeatmapMetric(null);
    handleClose();
  };

  const handleClose = () => closeDrawer(HEATMAP_DRAWER_ID);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      anchor="left"
      size="sm"
      variant="persistent"
      className="z-50 absolute left-0 top-0 bottom-0 h-full"
      data-testid="heatmap-controls-drawer"
      aria-label="Heatmap metrics selection"
    >
      <Drawer.Header className="bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center justify-between w-full">
          <h3
            id="heatmap-drawer-title"
            className="text-sm font-semibold text-neutral-800"
          >
            Heatmap Metrics
          </h3>
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={handleClose}
            icon={faXmark}
            aria-label="Close heatmap metrics drawer"
            data-testid="heatmap-close-button"
          />
        </div>
      </Drawer.Header>

      <Drawer.Content className="bg-white">
        <div role="listbox" aria-labelledby="heatmap-drawer-title">
          {/* All Opportunities option at the top */}
          <div className="border-b border-neutral-100">
            <button
              onClick={handleSelectAllOpportunities}
              role="option"
              aria-selected={isAllOpportunitiesSelected}
              data-testid="heatmap-all-opportunities-button"
              className={`
                w-full px-4 py-3 text-left text-sm transition-colors
                ${
                  isAllOpportunitiesSelected
                    ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                    : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
                }
              `}
            >
              All Opportunities
            </button>
          </div>

          {/* Metric options */}
          {Object.entries(groupedItems).map(([metricId, { label, options }]) => (
            <div
              key={metricId}
              className="border-b border-neutral-100"
              role="group"
              aria-label={label}
            >
              <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide bg-neutral-50">
                {label}
              </div>
              {options.map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => handleSelectMetric(opt)}
                  role="option"
                  aria-selected={selectedHeatmapMetric?.code === opt.code}
                  data-testid={`heatmap-metric-${opt.code}`}
                  className={`
                    w-full px-4 py-3 text-left text-sm transition-colors
                    ${
                      selectedHeatmapMetric?.code === opt.code
                        ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                        : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
                    }
                  `}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ))}

          {/* Empty state - only show if no metrics AND not showing all opportunities option */}
          {Object.keys(groupedItems).length === 0 && (
            <div
              className="px-4 py-8 text-center text-neutral-500 text-sm"
              data-testid="heatmap-empty-state"
            >
              No metrics available
            </div>
          )}
        </div>
      </Drawer.Content>

      {/* Footer with Turn Off button - only show when heatmap is active */}
      {showHeatmap && (
        <Drawer.Footer className="bg-neutral-50 border-t border-neutral-200 px-4 py-3">
          <button
            onClick={handleTurnOffHeatmap}
            aria-label="Turn off heatmap"
            data-testid="heatmap-turn-off-button"
            className="w-full px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-full hover:bg-neutral-50 transition-colors"
          >
            Turn Off Heatmap
          </button>
        </Drawer.Footer>
      )}
    </Drawer>
  );
};

export default HeatmapControls;
